// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { Injectable, TemplateRef } from '@angular/core';
import { Column } from '@lfx/shared/interfaces';
import { ViewState } from '@lfx/key-projects/key-projects.model';
import { DropdownMenuItem } from '@lfx/shared/components/dropdown-menu';
import { KeyProjectsTableEntity } from '@lfx/key-projects/entities/key-projects-table.entity';
import { InfluenceTooltipEntity } from '@lfx/key-projects/entities/influence-tooltip.entity';

/**
 * Configuration for table columns
 */
export interface TableColumnTemplates {
  projectColumn: TemplateRef<any>;
  healthScoreColumn: TemplateRef<any>;
  technicalInfluenceColumn: TemplateRef<any>;
  ecosystemInfluenceColumn: TemplateRef<any>;
  contributorsColumn: TemplateRef<any>;
  participantsColumn: TemplateRef<any>;
  activityColumn: TemplateRef<any>;
  actionsColumn: TemplateRef<any>;
}

/**
 * Service responsible for managing table column configuration
 * Follows Clean Architecture: Configuration logic separated from presentation
 */
@Injectable({ providedIn: 'root' })
export class TableConfigurationService {
  /**
   * Default sorting configuration
   * Centralized to prevent configuration drift between initial state and column definitions
   */
  public static readonly DEFAULT_SORT_COLUMN = 'contributors';
  public static readonly DEFAULT_SORT_DIRECTION: 'asc' | 'desc' = 'desc';

  // Shared tooltip content for both influence columns - uses centralized entity
  private readonly INFLUENCE_TOOLTIP_CONTENT = InfluenceTooltipEntity.getInfluenceTooltipHtml();

  /**
   * Generate table columns configuration based on view state and templates
   */
  getColumns(viewState: ViewState | null, templates: TableColumnTemplates): Column[] {
    const baseColumns: Column[] = [
      {
        name: this.getProjectColumnName(viewState),
        prop: 'projectInfo',
        template: templates.projectColumn,
        columnWidth: '200px',
        sortable: true,
        sortDir: 'asc',
      },
      {
        name: 'Health score',
        prop: 'healthScore',
        template: templates.healthScoreColumn,
        columnWidth: '120px',
        sortable: true,
        sortDir: 'asc',
      },
      {
        name: 'Technical influence',
        prop: 'technicalInfluence',
        sortProp: 'technicalInfluencePoints',
        template: templates.technicalInfluenceColumn,
        columnWidth: '180px',
        sortable: true,
        sortDir: 'desc',
        headerFaIcon: {
          icon: 'fal fa-info-circle',
          hoverContent: this.INFLUENCE_TOOLTIP_CONTENT,
        },
      },
      {
        name: 'Ecosystem influence',
        prop: 'ecosystemInfluence',
        sortProp: 'ecosystemInfluencePoints',
        template: templates.ecosystemInfluenceColumn,
        columnWidth: '180px',
        sortable: true,
        sortDir: 'desc',
        headerFaIcon: {
          icon: 'fal fa-info-circle',
          hoverContent: this.INFLUENCE_TOOLTIP_CONTENT,
        },
      },
      {
        name: 'Our contributors',
        prop: 'contributors',
        template: templates.contributorsColumn,
        columnWidth: '150px',
        sortable: true,
        sortDir: TableConfigurationService.DEFAULT_SORT_DIRECTION,
        headerFaIcon: {
          icon: 'fal fa-info-circle',
          hoverContent: `<span>Our <strong>Contributors</strong> are our employees who have contributed code to this project.</span>`,
        },
      },
      {
        name: 'Our participants',
        prop: 'participants',
        template: templates.participantsColumn,
        columnWidth: '150px',
        sortable: true,
        sortDir: 'desc',
        headerFaIcon: {
          icon: 'fal fa-info-circle',
          hoverContent: `<span>Our <strong>Participants</strong> are our employees who have been involved in this project in any way, including code contributions, documentation collaboration, events, training, etc.</span>`,
        },
      },
      {
        name: 'Our activity (2y)',
        prop: 'activity',
        template: templates.activityColumn,
        columnWidth: '180px',
        sortable: true,
        sortDir: 'asc',
      },
    ];

    // Add actions column only for workspace-projects view
    if (this.isWorkspaceMode(viewState)) {
      baseColumns.push({
        name: '',
        prop: 'actions',
        template: templates.actionsColumn,
        columnWidth: '16px',
      });
    }

    return baseColumns;
  }

  /**
   * Get the appropriate column name based on view state
   */
  private getProjectColumnName(viewState: ViewState | null): string {
    // Table always shows projects now
    return 'Project';
  }

  /**
   * Check if current view is workspace mode
   */
  isWorkspaceMode(viewState: ViewState | null): boolean {
    return viewState?.mode === 'workspace-projects';
  }

  /**
   * Get tooltip content for influence columns
   */
  getInfluenceTooltipContent(): string {
    return this.INFLUENCE_TOOLTIP_CONTENT;
  }

  /**
   * Get action menu items for workspace project operations
   * Delegates to entity for business logic
   */
  getActionMenuItems(): DropdownMenuItem[] {
    return KeyProjectsTableEntity.getActionMenuItems();
  }
}

